import React, { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { X, Download, Share } from 'lucide-react';

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already dismissed
    const dismissed = localStorage.getItem('pwaInstallDismissed');
    const dismissedTime = dismissed ? parseInt(dismissed, 10) : 0;
    const daysSinceDismissed = (Date.now() - dismissedTime) / (1000 * 60 * 60 * 24);

    // Don't show if standalone, or dismissed within last 7 days
    if (standalone || (dismissed && daysSinceDismissed < 7)) {
      return;
    }

    // For iOS, show custom prompt after delay
    if (iOS) {
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      return () => clearTimeout(timer);
    }

    // For Android/Desktop, listen for beforeinstallprompt
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after short delay
      setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwaInstallDismissed', Date.now().toString());
  };

  if (!showPrompt || isStandalone) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 z-50 animate-in slide-in-from-bottom duration-300">
      <div className="bg-white rounded-2xl shadow-xl border border-stone-200 p-4 max-w-lg mx-auto">
        <div className="flex items-start gap-3">
          {/* App Icon */}
          <div className="bg-amber-100 p-3 rounded-xl shrink-0">
            <Download className="h-6 w-6 text-amber-600" />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-stone-800">Install Bright Eyes App</h3>
            {isIOS ? (
              <p className="text-sm text-stone-500 mt-1">
                Tap <Share className="h-4 w-4 inline text-blue-500" /> then "Add to Home Screen" for quick access
              </p>
            ) : (
              <p className="text-sm text-stone-500 mt-1">
                Add to your home screen for faster access to your photos
              </p>
            )}
          </div>

          {/* Close Button */}
          <button 
            onClick={handleDismiss}
            className="text-stone-400 hover:text-stone-600 p-1"
            aria-label="Dismiss"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Action Buttons */}
        {!isIOS && deferredPrompt && (
          <div className="flex gap-2 mt-4">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={handleDismiss}
            >
              Not Now
            </Button>
            <Button 
              className="flex-1 btn-action"
              onClick={handleInstall}
            >
              <Download className="h-4 w-4 mr-2" />
              Install
            </Button>
          </div>
        )}

        {isIOS && (
          <div className="mt-3 pt-3 border-t border-stone-100">
            <div className="flex items-center gap-2 text-sm text-stone-600">
              <span className="bg-stone-100 px-2 py-1 rounded text-xs font-medium">1</span>
              <span>Tap the share button</span>
              <Share className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex items-center gap-2 text-sm text-stone-600 mt-2">
              <span className="bg-stone-100 px-2 py-1 rounded text-xs font-medium">2</span>
              <span>Select "Add to Home Screen"</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
